import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { VSCodeBrowserView } from "@/components/ai/chat/AgentExecutionConversation";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import type { FollowModeState } from "@/hooks/use-follow-mode";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { type EnvironmentState, stateFromEnvironment, canOpen } from "@/routes/environments/phase";
import type { FC } from "react";

type CodeTabState = EnvironmentState | "unavailable";

function getCodeTabState(environment: PlainEnvironment | undefined): CodeTabState {
    if (!environment) {
        return "unavailable";
    }
    return stateFromEnvironment(environment);
}

export type CodeTabPanelProps = {
    /** VS Code browser URL - only used when environment is running */
    vscodeUrl?: string;
    /** Follow mode state for VS Code */
    followMode?: FollowModeState;
    /** Whether this tab is currently visible */
    isVisible: boolean;
    /** The environment to display code for */
    environment?: PlainEnvironment;
    /** Callback when user clicks start environment button */
    onStartEnvironment?: () => void;
    /** Whether environment start is in progress */
    isStartingEnvironment?: boolean;
};

export const CodeTabPanel: FC<CodeTabPanelProps> = ({
    vscodeUrl,
    followMode,
    isVisible,
    environment,
    onStartEnvironment,
    isStartingEnvironment = false,
}) => {
    const envState = getCodeTabState(environment);
    const isTransitioning = envState === "starting" || envState === "stopping" || envState === "deleting";
    const canShowVSCode = envState === "running" && vscodeUrl && environment && canOpen(environment);

    return (
        <div
            id="code-panel"
            role="tabpanel"
            aria-labelledby="code-tab"
            aria-busy={isTransitioning || isStartingEnvironment}
            className={cn(
                "flex h-full min-h-0 flex-col",
                !isVisible && "hidden"
            )}
        >
            {canShowVSCode ? (
                <VSCodeBrowserView
                    editorUrl={vscodeUrl}
                    className="grow border-none"
                    followMode={followMode}
                    visible={isVisible}
                />
            ) : (
                <CodeTabEmptyState
                    envState={envState}
                    onStartEnvironment={onStartEnvironment}
                    isStartingEnvironment={isStartingEnvironment}
                />
            )}
        </div>
    );
};

type CodeTabEmptyStateProps = {
    envState: CodeTabState;
    onStartEnvironment?: () => void;
    isStartingEnvironment: boolean;
};

const CodeTabEmptyState: FC<CodeTabEmptyStateProps> = ({
    envState,
    onStartEnvironment,
    isStartingEnvironment,
}) => {
    switch (envState) {
        case "starting":
            return (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                    <IconSpinner size="lg" className="animate-spin text-content-secondary" />
                    <span className="text-content-secondary">Starting environment...</span>
                </div>
            );
        case "stopping":
            return (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                    <IconSpinner size="lg" className="animate-spin text-content-secondary" />
                    <span className="text-content-secondary">Environment is stopping...</span>
                </div>
            );
        case "deleting":
            return (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                    <IconSpinner size="lg" className="animate-spin text-content-secondary" />
                    <span className="text-content-secondary">Environment is being deleted...</span>
                </div>
            );
        case "stopped":
        case "deleted":
            return (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                    <span className="text-content-secondary">Environment is stopped</span>
                    {onStartEnvironment && (
                        <Button
                            variant="primary"
                            onClick={onStartEnvironment}
                            loading={isStartingEnvironment}
                            data-tracking-id="start-environment-code-tab"
                        >
                            Start environment
                        </Button>
                    )}
                </div>
            );
        case "unavailable":
        case "running":
        default:
            return (
                <div className="flex h-full flex-col items-center justify-center">
                    <span className="text-content-secondary">VS Code is not available</span>
                </div>
            );
    }
};
